import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Baby,
  HeartHandshake,
  Moon,
  Milk,
  BookOpen,
  Stethoscope,
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  Check,
  Star,
  ArrowUpRight,
  Quote,
  Instagram,
  Facebook,
} from "lucide-react";
import Img from "@/components/site/Img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Label = ({ children, testid }) => (
  <span
    data-testid={testid}
    className="font-mono-label text-xs uppercase tracking-[0.25em] text-[#D47753]"
  >
    {children}
  </span>
);

/* ---------------- Header ---------------- */
const navItems = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Pricing", href: "#pricing" },
  { label: "Stories", href: "#stories" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F7F5F0]/85 backdrop-blur-xl border-b border-[#1A1C19]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#home" data-testid="logo-link" className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[#D47753]/15 border border-[#D47753]/30 flex items-center justify-center shadow-sm">
            <span className="font-serif-display text-base tracking-tight text-[#D47753] leading-none">NC</span>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif-display text-xl tracking-tight">
              Newborn Care
            </span>
            <span className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[#D47753] mt-1">
              Beyond Expectation
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {navItems.map((n) => (
            <a
              key={n.label}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm text-[#5C6656] hover:text-[#1A1C19] transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://instagram.com/osakuejoan"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram"
            aria-label="Instagram"
            className="w-10 h-10 rounded-full border border-[#1A1C19]/10 flex items-center justify-center text-[#5C6656] hover:text-[#F7F5F0] hover:bg-[#D47753] hover:border-[#D47753] transition-all"
          >
            <Instagram className="w-5 h-5" strokeWidth={1.5} />
          </a>
          <a
            href="https://www.facebook.com/newborncarebeyondexpectation"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-facebook"
            aria-label="Facebook"
            className="w-10 h-10 rounded-full border border-[#1A1C19]/10 flex items-center justify-center text-[#5C6656] hover:text-[#F7F5F0] hover:bg-[#D47753] hover:border-[#D47753] transition-all"
          >
            <Facebook className="w-5 h-5" strokeWidth={1.5} />
          </a>
          <a
            href="#contact"
            data-testid="header-cta"
            className="inline-flex bg-[#2D4A22] text-[#F7F5F0] px-6 py-3 rounded-full text-sm font-medium transition-all hover:bg-[#1E3316] hover:-translate-y-0.5 shadow-sm"
          >
            Book Consultation
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#F7F5F0]/95 backdrop-blur-xl border-b border-[#1A1C19]/10 px-6 py-6 flex flex-col gap-4"
        >
          {navItems.map((n) => (
            <a
              key={n.label}
              href={n.href}
              onClick={() => setOpen(false)}
              className="text-base text-[#1A1C19]"
            >
              {n.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="bg-[#2D4A22] text-[#F7F5F0] px-6 py-3 rounded-full text-sm font-medium text-center mt-2"
          >
            Book Consultation
          </a>
          <a
            href="https://instagram.com/osakuejoan"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 text-base text-[#1A1C19] mt-1"
          >
            <Instagram className="w-6 h-6" strokeWidth={1.5} /> Instagram
          </a>
          <a
            href="https://www.facebook.com/newborncarebeyondexpectation"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 text-base text-[#1A1C19]"
          >
            <Facebook className="w-6 h-6" strokeWidth={1.5} /> Facebook
          </a>
        </div>
      )}
    </header>
  );
};

/* ---------------- Hero ---------------- */
const Hero = () => (
  <section id="home" data-testid="hero-section" className="relative pt-32 md:pt-40 pb-20 px-6 md:px-12">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <motion.div
        className="lg:col-span-6"
        initial="hidden"
        animate="show"
        variants={fadeUp}
      >
        <Label testid="hero-label">Registered Nurse Consultant</Label>
        <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-5 mb-6">
          Gentle, expert care for{" "}
          <span className="italic text-[#2D4A22]">mother</span> &{" "}
          <span className="italic text-[#D47753]">newborn</span>.
        </h1>
        <p className="text-lg text-[#5C6656] leading-relaxed max-w-xl mb-9">
          Newborn Care pairs you with a qualified registered nurse for
          personalised newborn and postpartum support — at home, on your
          schedule, beyond expectation.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="bg-[#2D4A22] text-[#F7F5F0] px-8 py-4 rounded-full font-medium transition-all hover:bg-[#1E3316] hover:-translate-y-1 shadow-sm"
          >
            Book a Consultation
          </a>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="border border-[#1A1C19]/20 text-[#1A1C19] px-8 py-4 rounded-full font-medium transition-all hover:bg-[#1A1C19]/5"
          >
            Explore Services
          </a>
        </div>
        <div className="flex items-center gap-6 mt-10">
          <div className="flex -space-x-3">
            {[1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className="w-10 h-10 rounded-full border-2 border-[#F7F5F0] bg-[#e3ddcf]"
              />
            ))}
          </div>
          <div>
            <div className="flex items-center gap-1 text-[#D47753]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-current" strokeWidth={0} />
              ))}
            </div>
            <p className="text-sm text-[#5C6656] mt-0.5">
              Trusted by 300+ families
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="lg:col-span-6"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="relative">
          <Img
            src="https://images.unsplash.com/photo-1544784179-ae1535e9f013?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Expecting mother gently cradling her pregnant belly"
            className="w-full h-[520px] object-cover rounded-[2rem] shadow-xl"
            fallbackClass="rounded-[2rem]"
          />
          <div className="absolute -bottom-6 -left-6 bg-[#FFFFFF] rounded-2xl p-5 shadow-lg border border-[#1A1C19]/10 hidden sm:block">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-[#2D4A22]/10 flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-[#2D4A22]" strokeWidth={1.5} />
              </span>
              <div>
                <p className="font-serif-display text-xl leading-none">25+ yrs</p>
                <p className="text-xs text-[#5C6656] mt-1">Clinical experience</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ---------------- Stats ---------------- */
const stats = [
  { value: "300+", label: "Families supported" },
  { value: "25+", label: "Years nursing" },
  { value: "On request", label: "Night support" },
  { value: "100%", label: "Personalised plans" },
];
const Stats = () => (
  <section data-testid="stats-section" className="px-6 md:px-12 py-10">
    <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
      {stats.map((s, i) => (
        <motion.div
          key={s.label}
          custom={i}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          className="bg-[#FFFFFF] border border-[#1A1C19]/10 rounded-2xl p-7 text-center"
        >
          <p className="font-serif-display text-4xl md:text-5xl text-[#2D4A22]">
            {s.value}
          </p>
          <p className="text-sm text-[#5C6656] mt-2">{s.label}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

/* ---------------- Services ---------------- */
const services = [
  {
    icon: Baby,
    title: "Newborn Care",
    desc: "Feeding, bathing, soothing and establishing gentle daily routines for your baby.",
    img: "https://images.unsplash.com/photo-1492725764893-90b379c2b6e7?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  },
  {
    icon: Milk,
    title: "Lactation Support",
    desc: "Hands-on breastfeeding guidance, latch help and bottle-feeding transitions.",
    img: "https://images.unsplash.com/photo-1567073931033-07972e6081bd?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  },
  {
    icon: HeartHandshake,
    title: "Postpartum Recovery",
    desc: "Compassionate care for mum — healing, wellbeing and emotional support.",
    img: "https://images.unsplash.com/photo-1542385151-efd9000785a0?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  },
  {
    icon: Moon,
    title: "Sleep Consulting",
    desc: "Healthy sleep foundations and routines tailored to your baby's stage.",
    img: "https://images.unsplash.com/photo-1626856295349-6f21d6e6cfef?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  },
  {
    icon: Stethoscope,
    title: "Overnight Nurse",
    desc: "Restful nights with professional in-home night nursing — available upon request.",
    img: "https://images.unsplash.com/photo-1546015720-b8b30df5aa27?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  },
  {
    icon: BookOpen,
    title: "Parent Education",
    desc: "Antenatal classes and confidence-building guidance for new parents.",
    img: "https://images.unsplash.com/photo-1528218635780-5952720c9729?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  },
];
const Services = () => (
  <section id="services" data-testid="services-section" className="px-6 md:px-12 py-24">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-14">
        <Label>What we offer</Label>
        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight mt-4">
          Complete care, from bump to baby.
        </h2>
        <p className="text-lg text-[#5C6656] mt-4">
          Every service is delivered by a registered nurse and shaped entirely
          around your family's needs.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((s, i) => (
          <motion.div
            key={s.title}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            data-testid={`service-card-${i}`}
            className="bg-[#FFFFFF] border border-[#1A1C19]/10 rounded-2xl overflow-hidden transition-transform hover:-translate-y-1.5 group"
          >
            <div className="relative h-44 overflow-hidden">
              <Img
                src={s.img}
                alt={s.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-[#F7F5F0]/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
                <s.icon className="w-5 h-5 text-[#2D4A22]" strokeWidth={1.5} />
              </span>
            </div>
            <div className="p-8">
              <h3 className="font-serif-display text-2xl mb-2">{s.title}</h3>
              <p className="text-[#5C6656] leading-relaxed">{s.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- About ---------------- */
const About = () => (
  <section id="about" data-testid="about-section" className="px-6 md:px-12 py-24 bg-[#EFEDE7]">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <motion.div
        className="lg:col-span-5"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fadeUp}
      >
        <Img
          src="https://images.unsplash.com/photo-1570657891791-e39a9d185540?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
          alt="Mother lovingly holding her newborn baby"
          className="w-full h-[480px] object-cover rounded-[2rem] shadow-lg"
          fallbackClass="rounded-[2rem]"
        />
      </motion.div>
      <motion.div
        className="lg:col-span-7"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fadeUp}
      >
        <Label>About Newborn Care</Label>
        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight mt-4 mb-6">
          Beyond expectation — because your family deserves it.
        </h2>
        <p className="text-lg text-[#5C6656] leading-relaxed mb-5">
          Newborn Care was founded by a registered nurse with over twenty-five years
          of clinical experience in neonatal and maternal health. We bring
          hospital-grade expertise into the warmth of your home.
        </p>
        <p className="text-[#5C6656] leading-relaxed mb-8">
          Our philosophy is simple: empower parents with knowledge, support
          mothers through recovery, and give every newborn the gentlest start in
          life.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            "Fully registered & insured",
            "Evidence-based, gentle methods",
            "In-home & flexible scheduling",
            "Day & overnight availability",
          ].map((p) => (
            <div key={p} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#2D4A22] flex items-center justify-center mt-0.5 shrink-0">
                <Check className="w-3.5 h-3.5 text-[#F7F5F0]" strokeWidth={2} />
              </span>
              <span className="text-[#1A1C19]">{p}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

/* ---------------- Process ---------------- */
const steps = [
  { n: "01", title: "Connect", desc: "Book a free intro call so we can understand your needs and hopes." },
  { n: "02", title: "Personal Plan", desc: "We design a tailored care plan around your family and routine." },
  { n: "03", title: "Hands-on Care", desc: "Your nurse delivers warm, professional support at home." },
  { n: "04", title: "Ongoing Support", desc: "We stay reachable for follow-ups, questions and reassurance." },
];
const Process = () => (
  <section id="process" data-testid="process-section" className="px-6 md:px-12 py-24">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-14">
        <Label>How it works</Label>
        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight mt-4">
          A calm, four-step journey.
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {steps.map((s, i) => (
          <motion.div
            key={s.n}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative bg-[#FFFFFF] border border-[#1A1C19]/10 rounded-2xl p-8"
          >
            <span className="font-serif-display text-5xl text-[#D47753]/40">
              {s.n}
            </span>
            <h3 className="font-serif-display text-2xl mt-3 mb-2">{s.title}</h3>
            <p className="text-[#5C6656] leading-relaxed">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Testimonials ---------------- */
const reviews = [
  {
    quote:
      "Our nurse felt like family from day one. The sleep guidance alone changed our lives — we finally rest at night.",
    name: "Aisha & Tom",
    role: "Parents of Layla",
  },
  {
    quote:
      "After a difficult birth, the postpartum care I received was tender and professional. I felt genuinely held.",
    name: "Priya M.",
    role: "First-time mum",
  },
  {
    quote:
      "Breastfeeding finally clicked thanks to their patient, expert lactation support. Beyond expectation indeed.",
    name: "Sofia R.",
    role: "Mum of twins",
  },
];
const Testimonials = () => (
  <section id="stories" data-testid="testimonials-section" className="px-6 md:px-12 py-24 bg-[#EFEDE7]">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-14">
        <Label>Family stories</Label>
        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight mt-4">
          Loved by the families we serve.
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {reviews.map((r, i) => (
          <motion.figure
            key={i}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            data-testid={`testimonial-${i}`}
            className="bg-[#FFFFFF] border border-[#1A1C19]/10 rounded-2xl p-8 flex flex-col"
          >
            <Quote className="w-8 h-8 text-[#D47753] mb-4" strokeWidth={1.5} />
            <blockquote className="text-[#1A1C19] leading-relaxed flex-1">
              "{r.quote}"
            </blockquote>
            <figcaption className="mt-6">
              <p className="font-serif-display text-xl">{r.name}</p>
              <p className="text-sm text-[#5C6656]">{r.role}</p>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Pricing ---------------- */
const plans = [
  {
    name: "Consultation",
    price: "$60",
    unit: "30-min session",
    desc: "A focused 1:1 session to address your most pressing questions.",
    features: ["30-minute consultation", "Personalised advice", "In-home or virtual", "Follow-up message"],
    featured: false,
  },
  {
    name: "In-Home Visit",
    price: "$150",
    unit: "2 hr minimum",
    desc: "Hands-on, in-home newborn and postpartum support when you need it most.",
    features: ["Minimum 2-hour visit", "Hands-on newborn care", "Feeding & soothing support", "Postpartum check-ins", "Flexible scheduling"],
    featured: true,
  },
  {
    name: "Custom Care Package",
    price: "$75",
    unit: "/hr · starting",
    desc: "Fully customised day or overnight care designed around your family.",
    features: ["Starting at $75 / hour", "Overnight care (8 hrs) — $600", "Daytime package — 8-hour blocks", "Dedicated nurse & flexible scheduling", "Multiples & special care welcome"],
    featured: false,
  },
];
const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="px-6 md:px-12 py-24">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-14">
        <Label>Care packages</Label>
        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight mt-4">
          Simple, transparent pricing.
        </h2>
        <p className="text-lg text-[#5C6656] mt-4">
          All prices in Canadian dollars (CAD). In-home visits have a 2-hour
          minimum.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        {plans.map((p, i) => (
          <motion.div
            key={p.name}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            data-testid={`pricing-card-${i}`}
            className={`rounded-3xl p-8 flex flex-col border ${
              p.featured
                ? "bg-[#1A1C19] text-[#F7F5F0] border-[#1A1C19]"
                : "bg-[#FFFFFF] text-[#1A1C19] border-[#1A1C19]/10"
            }`}
          >
            {p.featured && (
              <span className="self-start font-mono-label text-xs uppercase tracking-[0.2em] text-[#D47753] mb-4">
                Most loved
              </span>
            )}
            <h3 className="font-serif-display text-2xl">{p.name}</h3>
            <div className="flex items-end gap-2 mt-3">
              <span className="font-serif-display text-5xl">{p.price}</span>
              <span className={`text-sm mb-2 ${p.featured ? "text-[#F7F5F0]/60" : "text-[#5C6656]"}`}>
                {p.unit}
              </span>
            </div>
            <p className={`mt-3 mb-7 ${p.featured ? "text-[#F7F5F0]/75" : "text-[#5C6656]"}`}>
              {p.desc}
            </p>
            <ul className="space-y-3 mb-8 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm">
                  <Check
                    className={`w-4 h-4 shrink-0 ${p.featured ? "text-[#D47753]" : "text-[#2D4A22]"}`}
                    strokeWidth={2}
                  />
                  {f}
                </li>
              ))}
            </ul>
            <a
              href="#contact"
              data-testid={`pricing-cta-${i}`}
              className={`text-center px-6 py-3.5 rounded-full font-medium transition-all hover:-translate-y-0.5 ${
                p.featured
                  ? "bg-[#F7F5F0] text-[#1A1C19] hover:bg-white"
                  : "bg-[#2D4A22] text-[#F7F5F0] hover:bg-[#1E3316]"
              }`}
            >
              Get started
            </a>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Contact ---------------- */
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    country: "Canada",
    service: "Newborn Care",
    due_date: "",
    start_when: "As soon as possible",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        msg: "Thank you! We'll be in touch within 24 hours.",
      });
      setForm({ name: "", email: "", phone: "", city: "", country: "Canada", service: "Newborn Care", due_date: "", start_when: "As soon as possible", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        msg: "Something went wrong. Please try again or call us directly.",
      });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-6 md:px-12 py-24 bg-[#EFEDE7]">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Label>Get in touch</Label>
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight mt-4 mb-6">
            Let's care for your family together.
          </h2>
          <p className="text-lg text-[#5C6656] leading-relaxed mb-10">
            Reach out for a free 15-min, no-obligation chat. Tell us a little
            about your needs and we'll craft the right plan for you.
          </p>
          <div className="space-y-5">
            <a href="tel:+14375519351" data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full bg-[#2D4A22]/8 flex items-center justify-center group-hover:bg-[#2D4A22] transition-colors">
                <Phone className="w-5 h-5 text-[#2D4A22] group-hover:text-[#F7F5F0] transition-colors" strokeWidth={1.5} />
              </span>
              <span className="text-[#1A1C19]">437 551-9351</span>
            </a>
            <a href="mailto:newborncarebeyond@gmail.com" data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full bg-[#2D4A22]/8 flex items-center justify-center group-hover:bg-[#2D4A22] transition-colors">
                <Mail className="w-5 h-5 text-[#2D4A22] group-hover:text-[#F7F5F0] transition-colors" strokeWidth={1.5} />
              </span>
              <span className="text-[#1A1C19]">newborncarebeyond@gmail.com</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-[#2D4A22]/8 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#2D4A22]" strokeWidth={1.5} />
              </span>
              <span className="text-[#1A1C19]">In-home & virtual — flexible scheduling</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-[#FFFFFF] border border-[#1A1C19]/10 rounded-3xl p-8 md:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Your name" testid="input-name">
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name"
                  className="form-in"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Email" testid="input-email">
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email-input"
                  className="form-in"
                  placeholder="jane@email.com"
                />
              </Field>
              <Field label="Phone" testid="input-phone">
                <input
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="contact-phone-input"
                  className="form-in"
                  placeholder="Optional"
                />
              </Field>
              <Field label="Service needed" testid="input-service">
                <select
                  value={form.service}
                  onChange={update("service")}
                  data-testid="contact-service"
                  className="form-in"
                >
                  {["Newborn Care", "Lactation Support", "Postpartum Recovery", "Sleep Consulting", "Overnight Nurse", "Parent Education"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="City" testid="input-city">
                <input
                  value={form.city}
                  onChange={update("city")}
                  data-testid="contact-city"
                  className="form-in"
                  placeholder="Toronto"
                />
              </Field>
              <Field label="Country" testid="input-country">
                <input
                  value={form.country}
                  onChange={update("country")}
                  data-testid="contact-country"
                  className="form-in"
                  placeholder="Canada"
                />
              </Field>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Due / baby's birth date" testid="input-date">
                <input
                  type="date"
                  value={form.due_date}
                  onChange={update("due_date")}
                  data-testid="contact-date"
                  className="form-in"
                />
              </Field>
              <Field label="When would you like to start?" testid="input-start">
                <select
                  value={form.start_when}
                  onChange={update("start_when")}
                  data-testid="contact-start"
                  className="form-in"
                >
                  {["As soon as possible", "Within 2 weeks", "Within 1 month", "1–3 months", "After my baby arrives", "Flexible / just exploring"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="mt-5">
              <Field label="How can we help?" testid="input-message">
                <textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message"
                  className="form-in resize-none"
                  placeholder="Tell us about your needs..."
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={status.state === "loading"}
              data-testid="contact-submit"
              className="mt-7 w-full bg-[#2D4A22] text-[#F7F5F0] px-8 py-4 rounded-full font-medium transition-all hover:bg-[#1E3316] disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {status.state === "loading" ? "Sending..." : "Send enquiry"}
              <ArrowUpRight className="w-5 h-5" strokeWidth={1.5} />
            </button>

            {status.state === "success" && (
              <p data-testid="contact-success" className="mt-4 text-center text-[#2D4A22] font-medium">
                {status.msg}
              </p>
            )}
            {status.state === "error" && (
              <p data-testid="contact-error" className="mt-4 text-center text-red-600">
                {status.msg}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-label text-xs uppercase tracking-[0.15em] text-[#5C6656] mb-2 block">
      {label}
    </span>
    {children}
  </label>
);

/* ---------------- Footer ---------------- */
const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#1A1C19] text-[#F7F5F0] px-6 md:px-12 pt-24 pb-12">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16 border-b border-[#F7F5F0]/10">
        <div className="md:col-span-5">
          <span className="font-serif-display text-3xl">Newborn Care</span>
          <p className="text-[#F7F5F0]/60 mt-4 max-w-sm leading-relaxed">
            Registered nurse consultancy for mother and baby care. Beyond
            expectation, every step of the way.
          </p>
          <div className="flex items-center gap-4 mt-6">
            <a
              href="https://instagram.com/osakuejoan"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="Instagram"
              className="w-12 h-12 rounded-full border border-[#F7F5F0]/20 flex items-center justify-center text-[#F7F5F0]/80 hover:text-[#F7F5F0] hover:bg-[#D47753] hover:border-[#D47753] transition-all"
            >
              <Instagram className="w-6 h-6" strokeWidth={1.5} />
            </a>
            <a
              href="https://www.facebook.com/newborncarebeyondexpectation"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              aria-label="Facebook"
              className="w-12 h-12 rounded-full border border-[#F7F5F0]/20 flex items-center justify-center text-[#F7F5F0]/80 hover:text-[#F7F5F0] hover:bg-[#D47753] hover:border-[#D47753] transition-all"
            >
              <Facebook className="w-6 h-6" strokeWidth={1.5} />
            </a>
            <span className="text-sm text-[#F7F5F0]/60">@osakuejoan</span>
          </div>
        </div>
        <div className="md:col-span-3 md:col-start-7">
          <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-[#D47753] mb-4">Explore</p>
          <ul className="space-y-3 text-[#F7F5F0]/70">
            {navItems.map((n) => (
              <li key={n.label}>
                <a href={n.href} className="hover:text-[#F7F5F0] transition-colors">{n.label}</a>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-[#D47753] mb-4">Contact</p>
          <ul className="space-y-3 text-[#F7F5F0]/70">
            <li>437 551-9351</li>
            <li>newborncarebeyond@gmail.com</li>
            <li>In-home visits regionwide</li>
          </ul>
        </div>
      </div>
      <p className="font-serif-display text-[14vw] leading-none tracking-tight text-[#F7F5F0]/90 mt-10">
        Newborn<span className="text-[#D47753]">.</span>
      </p>
      <p className="text-[#F7F5F0]/40 text-sm mt-8">
        © {new Date().getFullYear()} Newborn Care — Beyond Expectation. All rights reserved.
      </p>
    </div>
  </footer>
);

/* ---------------- Page ---------------- */
const Landing = () => (
  <div className="App grain">
    <Header />
    <Hero />
    <Stats />
    <Services />
    <About />
    <Process />
    <Testimonials />
    <Pricing />
    <Contact />
    <Footer />
  </div>
);

export default Landing;
